// Der Posteingang: alles, was von aussen hereinkommt, an einem Ort.
//
// Die Seite beantwortet drei Fragen, in dieser Reihenfolge:
//   1. Liegt etwas an?          – die Zahlen oben
//   2. Was genau?               – die Liste links
//   3. Und was tue ich damit?   – die Spalte rechts
//
// Erledigt wird immer dort, wo die Sache hingehört: Ein Musikwunsch wird über
// die Wunschliste angenommen, ein Termin im Kalender freigegeben. Diese Seite
// ruft dieselben Wege auf, die auch die Modulseiten benutzen - sie erfindet
// keine zweite Wahrheit darüber, was „angenommen" bedeutet.
//
// Aufbau wie ein Postfach: links die Liste (Quelle als Symbol, Absender,
// Vorschau, Zeit, Punkt für ungelesen), rechts die geöffnete Sache. Auf dem
// Telefon steht immer nur eines von beiden da – die Liste oder die Sache.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  api, posteingangApi, voiceSurveyApi,
  type PosteingangArt, type PosteingangDetail, type PosteingangEintrag, type StummerAbsender,
} from "../api";
import { errorText } from "../components/streaming-ui";
import { Meldungen, Schalter, Sym } from "../components/social-ui";
import { Badge, Callout, EmptyState, Page, PageHeader, Tabs } from "../components/ui";
import type { TeamMember } from "../types";

/** Jede Quelle hat ihr Symbol und ihren Farbton – so erkennt man sie in der Liste ohne zu lesen. */
const ART_FARBE: Record<PosteingangArt, string> = {
  kontakt: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
  studio: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
  wunsch: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  oton: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  veranstaltung: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  stoerung: "bg-red-500/10 text-red-600 dark:text-red-300",
};

const ART_SYMBOL: Record<PosteingangArt, string> = {
  kontakt: "mail",
  studio: "chat",
  wunsch: "music",
  oton: "mic",
  veranstaltung: "calendar",
  stoerung: "alert",
};

/** Wohin es weitergeht, wenn jemand die ganze Sache sehen will. */
const ART_MODUL: Record<PosteingangArt, { to: string; label: string } | null> = {
  kontakt: null,
  studio: null,
  wunsch: null,
  oton: { to: "/frage-der-woche", label: "Frage der Woche" },
  veranstaltung: { to: "/kalender", label: "Mitmach-Kalender" },
  stoerung: { to: "/servers", label: "Server & Sendung" },
};

const STATUS_TEXT: Record<string, string> = { neu: "neu", in_arbeit: "in Arbeit", erledigt: "erledigt" };
const STATUS_TON: Record<string, "brand" | "warning" | "success"> = { neu: "brand", in_arbeit: "warning", erledigt: "success" };

function wann(iso: string): string {
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} min`;
  if (min < 60 * 24) return `vor ${Math.round(min / 60)} h`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function feldText(wert: unknown): string {
  if (wert === null || wert === undefined || wert === "") return "–";
  if (typeof wert === "boolean") return wert ? "ja" : "nein";
  if (typeof wert === "string" && /^\d{4}-\d{2}-\d{2}T/.test(wert)) return new Date(wert).toLocaleString("de-DE");
  return String(wert);
}

/** Klarnamen für die Felder der Quelle – „listener_name" liest sich schlecht. */
const FELD_NAMEN: Record<string, string> = {
  name: "Name", email: "E-Mail", subject: "Betreff", body: "Nachricht",
  listener_name: "Name", place: "Ort", message: "Nachricht", status: "Stand",
  kind: "Art", duration_ms: "Länge", hat_audio: "Aufnahme", created_at: "Eingegangen",
  title: "Titel", artist: "Interpret", keyword: "Stichwort",
  starts_at: "Beginn", ends_at: "Ende", address: "Adresse", organizer: "Veranstalter",
  description: "Beschreibung", admission: "Eintritt", link: "Link",
  contact_name: "Kontakt", contact_mail: "E-Mail", contact_phone: "Telefon",
  started_at: "Seit", resolved_at: "Behoben",
};

/** Rundes Symbol der Quelle. */
function ArtSymbol({ art, gross = false }: { art: PosteingangArt; gross?: boolean }): JSX.Element {
  return (
    <span className={`flex flex-shrink-0 items-center justify-center rounded-full ${ART_FARBE[art]} ${gross ? "h-10 w-10" : "h-8 w-8"}`}>
      <Sym name={ART_SYMBOL[art]} className={gross ? "h-5 w-5" : "h-4 w-4"} />
    </span>
  );
}

export function PosteingangPage(): JSX.Element {
  const [arten, setArten] = useState<Record<PosteingangArt, string> | null>(null);
  const [liste, setListe] = useState<PosteingangEintrag[]>([]);
  const [zahlen, setZahlen] = useState<{ neu: number; offen: number; nach_art: Partial<Record<PosteingangArt, number>> } | null>(null);
  const [status, setStatus] = useState<string>("offen");
  const [art, setArt] = useState<PosteingangArt | "">("");
  const [nurMeine, setNurMeine] = useState(false);
  const [offen, setOffen] = useState<string | null>(null);
  const [detail, setDetail] = useState<PosteingangDetail | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [stumme, setStumme] = useState<StummerAbsender[]>([]);
  const [zeigeStumme, setZeigeStumme] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      const [l, z] = await Promise.all([
        posteingangApi.liste({ status, art: art || undefined, mir: nurMeine }),
        posteingangApi.zaehler(),
      ]);
      setArten(l.arten);
      setListe(l.eintraege);
      setZahlen(z);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [status, art, nurMeine]);

  useEffect(() => void laden(), [laden]);
  useEffect(() => { api.listTeam().then(setTeam).catch(() => setTeam([])); }, []);

  // Alle zwei Minuten nachsehen. Häufiger wäre Unruhe: Der Posteingang ist
  // kein Live-Bild, sondern eine Ablage.
  useEffect(() => {
    const t = setInterval(() => void laden(), 120_000);
    return () => clearInterval(t);
  }, [laden]);

  useEffect(() => {
    if (!offen) { setDetail(null); return; }
    posteingangApi.detail(offen).then(setDetail).catch((err) => setError(errorText(err)));
  }, [offen]);

  const tun = async (fn: () => Promise<unknown>, gut?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setHinweis(null);
    try {
      await fn();
      if (gut) setHinweis(gut);
      await laden();
      if (offen) setDetail(await posteingangApi.detail(offen).catch(() => null));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Die Handlung, die zur Art gehört – über die Route des jeweiligen Moduls.
   * Gibt `null`, wo es keine gibt (eine Kontaktanfrage beantwortet man, man
   * nimmt sie nicht „auf Sendung").
   */
  const sendeHandlung = (e: PosteingangEintrag): { text: string; run: () => Promise<unknown> } | null => {
    switch (e.art) {
      case "studio":
        return { text: "Auf Sendung genommen", run: () => api.setMessageStatus(e.quelle_id, "aired") };
      case "wunsch":
        return { text: "Wunsch annehmen", run: () => api.acceptRequest(e.quelle_id) };
      case "oton":
        return { text: "Für den Block freigeben", run: () => voiceSurveyApi.patchAnswer(e.quelle_id, { status: "approved" }) };
      default:
        return null;
    }
  };

  const statusReiter = (["offen", "neu", "in_arbeit", "erledigt", "alle"] as const).map((s) => ({
    value: s,
    label: s === "offen" ? "Offen" : s === "alle" ? "Alle" : s === "in_arbeit" ? "In Arbeit" : s === "neu" ? "Neu" : "Erledigt",
    count: s === "offen" ? zahlen?.offen : s === "neu" ? zahlen?.neu : undefined,
  }));

  return (
    <Page>
      <PageHeader
        eyebrow="Hörer"
        title={
          <span className="flex items-center gap-3">
            Posteingang
            {zahlen && zahlen.neu > 0 && <Badge tone="brand">{zahlen.neu} neu</Badge>}
          </span>
        }
        description="Kontaktanfragen, Nachrichten ins Studio, Musikwünsche, O-Töne, eingereichte Termine und Störungen – alles hier, nichts im Mailfach. Erledigtes verschwindet aus der Liste, bleibt aber auffindbar."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-full overflow-x-auto">
            <Tabs value={status} onChange={(s) => { setStatus(s); setOffen(null); }} items={statusReiter} />
          </div>
          <label className="flex items-center gap-2.5 text-sm text-secondary">
            <Schalter checked={nurMeine} label="nur meine" onChange={(v) => { setNurMeine(v); setOffen(null); }} />
            nur meine
          </label>
        </div>

        {arten && (
          <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap">
            <button
              onClick={() => { setArt(""); setOffen(null); }}
              className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition ${art === "" ? "border-transparent bg-[var(--text-primary)] text-[var(--bg-card)]" : "border-default bg-card text-secondary hover:bg-[var(--bg-hover)]"}`}
            >
              Alle Quellen
            </button>
            {(Object.keys(arten) as PosteingangArt[]).map((a) => (
              <button
                key={a}
                onClick={() => { setArt(a === art ? "" : a); setOffen(null); }}
                className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition ${art === a ? "border-transparent bg-[var(--text-primary)] text-[var(--bg-card)]" : "border-default bg-card text-secondary hover:bg-[var(--bg-hover)]"}`}
              >
                <Sym name={ART_SYMBOL[a]} className="h-3.5 w-3.5" />
                {arten[a]}
                {zahlen?.nach_art[a] ? <span className="tabular-nums opacity-70">{zahlen.nach_art[a]}</span> : null}
              </button>
            ))}
          </div>
        )}
      </PageHeader>

      <Meldungen error={error} notice={hinweis} onClear={() => setHinweis(null)} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)]">
        {/* ---------- Liste ---------- */}
        <div className={`space-y-4 ${offen ? "hidden lg:block" : ""}`}>
          <section className="card overflow-hidden">
            {liste.length === 0 ? (
              <div className="p-3">
                <EmptyState
                  icon={<Sym name="inbox" className="h-5 w-5" />}
                  title={status === "offen" ? "Nichts offen. Ein guter Zustand." : "Hier ist nichts."}
                  description={status === "offen" ? "Sobald etwas hereinkommt, steht es hier." : undefined}
                />
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {liste.map((e) => {
                  const gewaehlt = offen === e.id;
                  const ungelesen = !e.gelesen_at;
                  return (
                    <li key={e.id}>
                      <button
                        onClick={() => setOffen(e.id === offen ? null : e.id)}
                        className={`relative flex w-full gap-3 px-4 py-3 text-left transition ${gewaehlt ? "bg-brand-primary/[0.07]" : "hover:bg-[var(--bg-hover)]"}`}
                      >
                        {gewaehlt && <span className="absolute inset-y-0 left-0 w-0.5 bg-brand-primary" aria-hidden="true" />}
                        <ArtSymbol art={e.art} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`min-w-0 flex-1 truncate text-sm ${ungelesen ? "font-semibold text-primary" : "font-medium text-secondary"}`}>
                              {e.absender ?? "ohne Namen"}
                            </span>
                            <span className="flex-shrink-0 text-xs tabular-nums text-muted">{wann(e.eingegangen_at)}</span>
                            {ungelesen && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-primary" title="neu" aria-label="neu" />}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
                            <span>{arten?.[e.art] ?? e.art}</span>
                            {e.channel_name && <><span aria-hidden="true">·</span><span className="truncate">{e.channel_name}</span></>}
                          </div>
                          {e.vorschau && (
                            <div className={`mt-1 line-clamp-2 text-sm ${ungelesen ? "text-secondary" : "text-muted"}`}>{e.vorschau}</div>
                          )}
                          {(e.zugewiesen_name || e.status !== "neu") && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {e.status !== "neu" && <Badge tone={STATUS_TON[e.status] ?? "neutral"}>{STATUS_TEXT[e.status]}</Badge>}
                              {e.zugewiesen_name && (
                                <span className="inline-flex items-center gap-1 text-xs text-muted">
                                  <Sym name="user" className="h-3 w-3" />{e.zugewiesen_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="card">
            <button
              onClick={() => {
                const naechste = !zeigeStumme;
                setZeigeStumme(naechste);
                if (naechste) posteingangApi.stummListe().then((r) => setStumme(r.stumm)).catch(() => setStumme([]));
              }}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-secondary hover:text-primary"
            >
              <Sym name="ban" className="h-4 w-4 text-muted" />
              <span className="flex-1">Stummgeschaltete Absender {zeigeStumme ? "ausblenden" : "anzeigen"}</span>
              <Sym name={zeigeStumme ? "up" : "down"} className="h-4 w-4 text-muted" />
            </button>
            {zeigeStumme && (
              <ul className="space-y-2 border-t border-default px-4 py-3 text-xs text-muted">
                {stumme.length === 0 && <li>Niemand. Bisher hat sich also niemand danebenbenommen.</li>}
                {stumme.map((s) => (
                  <li key={s.client_hash} className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-input px-1.5 py-0.5 font-mono text-secondary">{s.client_hash.slice(0, 12)}…</code>
                    {s.grund && <span>{s.grund}</span>}
                    <span>{s.bis ? `bis ${new Date(s.bis).toLocaleDateString("de-DE")}` : "dauerhaft"}</span>
                    <button
                      className="btn btn-secondary btn-sm ml-auto"
                      onClick={() => void tun(
                        async () => {
                          await posteingangApi.stummAufheben(s.client_hash);
                          setStumme((alt) => alt.filter((x) => x.client_hash !== s.client_hash));
                        },
                        "Absender wieder zugelassen."
                      )}
                    >
                      wieder zulassen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* ---------- Die geöffnete Sache ---------- */}
        <div className={offen ? "" : "hidden lg:block"}>
          {!detail ? (
            <div className="card flex min-h-[18rem] items-center justify-center p-6">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-input text-muted">
                  <Sym name="inbox" className="h-6 w-6" />
                </div>
                <p className="text-sm text-muted">
                  {offen ? "Wird geladen …" : "Links etwas auswählen, dann steht hier alles dazu."}
                </p>
              </div>
            </div>
          ) : (
            <Detail
              detail={detail}
              team={team}
              busy={busy}
              modul={ART_MODUL[detail.eintrag.art]}
              handlung={sendeHandlung(detail.eintrag)}
              tun={tun}
              onZurueck={() => setOffen(null)}
            />
          )}
        </div>
      </div>
    </Page>
  );
}

function Detail({ detail, team, busy, modul, handlung, tun, onZurueck }: {
  detail: PosteingangDetail;
  team: TeamMember[];
  busy: boolean;
  modul: { to: string; label: string } | null;
  handlung: { text: string; run: () => Promise<unknown> } | null;
  tun: (fn: () => Promise<unknown>, gut?: string) => Promise<void>;
  /** Nur auf dem Telefon: zurück zur Liste. */
  onZurueck: () => void;
}): JSX.Element {
  const e = detail.eintrag;
  const [antwort, setAntwort] = useState("");

  useEffect(() => setAntwort(""), [e.id]);

  const senden = async (ev: FormEvent): Promise<void> => {
    ev.preventDefault();
    if (antwort.trim().length < 2) return;
    await tun(() => posteingangApi.antworten(e.id, antwort.trim()), `Antwort an ${detail.antwort_an} ist raus.`);
    setAntwort("");
  };

  return (
    <section className="card animate-fade-in overflow-hidden">
      {/* Kopf: wer, was, wann */}
      <div className="border-b border-default px-5 py-4">
        <button onClick={onZurueck} className="btn btn-ghost btn-sm -ml-2 mb-2 lg:hidden">
          <Sym name="back" className="h-4 w-4" /> Zur Liste
        </button>
        <div className="flex items-start gap-3">
          <ArtSymbol art={e.art} gross />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-primary">{e.absender ?? "ohne Namen"}</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
              <span>{detail.art_text}</span>
              <span aria-hidden="true">·</span>
              <span>{new Date(e.eingegangen_at).toLocaleString("de-DE")}</span>
              {e.channel_name && <><span aria-hidden="true">·</span><span>{e.channel_name}</span></>}
            </div>
          </div>
          <Badge tone={STATUS_TON[e.status] ?? "neutral"} dot>{STATUS_TEXT[e.status]}</Badge>
        </div>
      </div>

      <div className="space-y-5 px-5 py-4">
        {detail.quelle_fehlt ? (
          <Callout tone="warning">
            Die Sache selbst gibt es nicht mehr – sie wurde gelöscht oder ist abgelaufen.
            Geblieben ist: „{e.vorschau ?? "ohne Vorschau"}"
          </Callout>
        ) : (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 rounded-lg bg-input p-4 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]" style={{ boxShadow: "inset 0 0 0 1px var(--border)" }}>
            {Object.entries(detail.quelle ?? {}).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-xs font-medium text-muted sm:pt-0.5">{FELD_NAMEN[k] ?? k}</dt>
                <dd className="mb-1 whitespace-pre-wrap break-words text-primary sm:mb-0">{feldText(v)}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Handlungen */}
        <div className="flex flex-wrap items-center gap-2">
          {handlung && (
            <button
              disabled={busy}
              onClick={() => void tun(async () => { await handlung.run(); await posteingangApi.aendern(e.id, { status: "erledigt" }); }, `${handlung.text}.`)}
              className="btn btn-primary"
            >
              <Sym name="check" className="h-4 w-4" />
              {handlung.text}
            </button>
          )}
          {e.status !== "in_arbeit" && (
            <button
              disabled={busy}
              onClick={() => void tun(() => posteingangApi.aendern(e.id, { status: "in_arbeit" }), "Übernommen.")}
              className="btn btn-secondary"
            >
              Ich kümmere mich
            </button>
          )}
          {e.status !== "erledigt" ? (
            <button
              disabled={busy}
              onClick={() => void tun(() => posteingangApi.aendern(e.id, { status: "erledigt" }), "Erledigt.")}
              className="btn btn-secondary"
            >
              Erledigt
            </button>
          ) : (
            <button
              disabled={busy}
              onClick={() => void tun(() => posteingangApi.aendern(e.id, { status: "neu" }), "Wieder offen.")}
              className="btn btn-secondary"
            >
              Wieder öffnen
            </button>
          )}
          {modul && (
            <Link to={modul.to} className="btn btn-ghost">
              In „{modul.label}" öffnen
              <Sym name="external" className="h-3.5 w-3.5" />
            </Link>
          )}
          {e.client_hash && (
            <button
              disabled={busy}
              onClick={() => {
                const grund = window.prompt("Diesen Absender stummschalten. Grund (freiwillig):");
                if (grund === null) return;
                void tun(
                  () => posteingangApi.stumm(e.id, { grund: grund || undefined }),
                  "Von diesem Gerät kommt nichts mehr in den Posteingang."
                );
              }}
              className="btn btn-ghost text-red-600 hover:!text-red-700 dark:text-red-400 sm:ml-auto"
            >
              <Sym name="ban" className="h-4 w-4" />
              Absender stummschalten
            </button>
          )}
        </div>

        <label className="block max-w-sm">
          <span className="field-label">Zuständig</span>
          <select
            className="input"
            value={e.zugewiesen_an ?? ""}
            onChange={(ev) => void tun(() => posteingangApi.aendern(e.id, { zugewiesen_an: ev.target.value || null }))}
          >
            <option value="">niemand</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>{m.display_name || m.email}</option>
            ))}
          </select>
        </label>

        {/* Bisherige Antworten als Verlauf – ausgehend, also rechtsbündig */}
        {detail.antworten.length > 0 && (
          <div className="space-y-3 border-t border-default pt-4">
            <div className="section-label">Verlauf</div>
            <ul className="space-y-3">
              {detail.antworten.map((a) => (
                <li key={a.id} className="flex flex-col items-end">
                  <div className="max-w-[90%] rounded-2xl rounded-br-md bg-brand-primary/10 px-4 py-2.5 text-sm text-primary">
                    <div className="whitespace-pre-wrap break-words">{a.text}</div>
                  </div>
                  <div className="mt-1 text-right text-[11px] text-muted">
                    an {a.an} · {new Date(a.angelegt_at).toLocaleString("de-DE")}
                    {a.verfasst_von ? ` · ${a.verfasst_von}` : ""}
                    {a.gesendet_at ? " · gesendet" : a.fehler ? " · nicht angekommen" : " · unterwegs"}
                  </div>
                  {a.fehler && <div className="mt-0.5 text-xs text-red-600 dark:text-red-400">{a.fehler}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {detail.antwort_an ? (
        <form onSubmit={senden} className="border-t border-default bg-input px-5 py-4">
          <label className="block">
            <span className="field-label">Antwort an {detail.antwort_an}</span>
            <textarea
              className="input bg-card"
              rows={4}
              value={antwort}
              onChange={(ev) => setAntwort(ev.target.value)}
              placeholder="Hallo …"
            />
          </label>
          <div className="mt-3 flex justify-end">
            <button type="submit" disabled={busy || antwort.trim().length < 2} className="btn btn-primary">
              <Sym name="send" className="h-4 w-4" />
              Antworten und erledigen
            </button>
          </div>
        </form>
      ) : (
        <p className="border-t border-default bg-input px-5 py-3 text-xs text-muted">
          Keine Adresse dabei – hier lässt sich nicht antworten.
        </p>
      )}
    </section>
  );
}
